import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Users, Search, Edit, Lock, Unlock, Trash2 } from 'lucide-react';
import axios from 'axios';
import { formatDate } from '../../utils/dateTimeUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : ''
  };
};

const MemberList = () => {
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [blockModal, setBlockModal] = useState({ open: false, member: null });
  const [blockReason, setBlockReason] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  useEffect(() => {
    fetchMembers();
  }, []);

  useEffect(() => {
    filterMembers();
  }, [members, searchTerm]);

  const fetchMembers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/library/members`, { headers: getAuthHeaders() });
      setMembers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching members:', error);
      setMembers([]);
    }
  };

  const filterMembers = () => {
    if (!searchTerm) {
      setFilteredMembers(members);
      return;
    }

    const filtered = members.filter(member =>
      member.user_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.member_card_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.user_type.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredMembers(filtered);
  };

  // Pagination calculations
  const totalRecords = filteredMembers.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalRecords);
  const paginatedMembers = filteredMembers.slice(startIndex, endIndex);

  // Reset page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleBlockMember = async () => {
    if (!blockModal.member || !blockReason) {
      alert('Please provide a reason for blocking');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/library/members/${blockModal.member.id}/block?reason=${encodeURIComponent(blockReason)}`,
        { method: 'PUT' }
      );

      if (response.ok) {
        alert('Member blocked successfully');
        setBlockModal({ open: false, member: null });
        setBlockReason('');
        fetchMembers();
      } else {
        alert('Failed to block member');
      }
    } catch (error) {
      console.error('Error blocking member:', error);
      alert('Failed to block member');
    } finally {
      setLoading(false);
    }
  };

  const handleUnblockMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to unblock this member?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/library/members/${memberId}/unblock`, {
        method: 'PUT'
      });

      if (response.ok) {
        alert('Member unblocked successfully');
        fetchMembers();
      } else {
        alert('Failed to unblock member');
      }
    } catch (error) {
      console.error('Error unblocking member:', error);
      alert('Failed to unblock member');
    }
  };

  const handleDeleteMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to delete this member? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/library/members/${memberId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        alert('Member deleted successfully');
        fetchMembers();
      } else {
        alert('Failed to delete member');
      }
    } catch (error) {
      console.error('Error deleting member:', error);
      alert('Failed to delete member');
    }
  };

  const getStatusBadge = (member) => {
    if (member.is_blocked) {
      return <span className="px-2 py-1 rounded text-xs bg-red-100 text-red-800">Blocked</span>;
    }
    return <span className="px-2 py-1 rounded text-xs bg-green-100 text-green-800">Active</span>;
  };

  // Bug fix: removed shadowing formatDate – using imported one

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Library Members ({filteredMembers.length})
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search by name, card number, or type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Members Table */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3">Card Number</th>
                  <th className="text-left p-3">Name</th>
                  <th className="text-left p-3">Type</th>
                  <th className="text-left p-3">Max Books</th>
                  <th className="text-left p-3">Joined Date</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedMembers.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center p-4 text-gray-500">
                      No members found
                    </td>
                  </tr>
                ) : (
                  paginatedMembers.map((member) => (
                    <tr key={member.id} className="border-b hover:bg-gray-50">
                      <td className="p-3 font-medium">{member.member_card_number}</td>
                      <td className="p-3">
                        <div className="font-medium">{member.user_name}</div>
                        <div className="text-sm text-gray-600">ID: {member.user_id}</div>
                      </td>
                      <td className="p-3">{member.user_type}</td>
                      <td className="p-3">{member.max_books_allowed}</td>
                      <td className="p-3">{formatDate(member.joined_date)}</td>
                      <td className="p-3">
                        {getStatusBadge(member)}
                        {member.is_blocked && member.blocked_reason && (
                          <div className="text-xs text-red-600 mt-1">
                            Reason: {member.blocked_reason}
                          </div>
                        )}
                      </td>
                      <td className="p-3">
                        <div className="flex gap-1">
                          {!member.is_blocked ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600"
                              onClick={() => setBlockModal({ open: true, member })}
                            >
                              <Lock className="w-3 h-3" />
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-green-600"
                              onClick={() => handleUnblockMember(member.id)}
                            >
                              <Unlock className="w-3 h-3" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDeleteMember(member.id)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalRecords > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>Show</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="border rounded px-2 py-1"
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span>entries | Showing {startIndex + 1} to {endIndex} of {totalRecords}</span>
              </div>
              
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>First</Button>
                  <Button variant="outline" size="sm" onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1}>Previous</Button>
                  <span className="px-3 py-1 text-sm">Page {currentPage} of {totalPages}</span>
                  <Button variant="outline" size="sm" onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages}>Next</Button>
                  <Button variant="outline" size="sm" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>Last</Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Block Member Modal */}
      <Dialog open={blockModal.open} onOpenChange={(open) => setBlockModal({ open, member: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Block Member</DialogTitle>
          </DialogHeader>
          {blockModal.member && (
            <div className="space-y-4">
              <div className="p-4 bg-red-50 rounded-lg space-y-2">
                <p><strong>Member:</strong> {blockModal.member.user_name}</p>
                <p><strong>Card Number:</strong> {blockModal.member.member_card_number}</p>
                <p><strong>Type:</strong> {blockModal.member.user_type}</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Reason for Blocking *</label>
                <textarea
                  required
                  rows="3"
                  value={blockReason}
                  onChange={(e) => setBlockReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter the reason for blocking this member..."
                />
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setBlockModal({ open: false, member: null })}
                >
                  Cancel
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={handleBlockMember} 
                  disabled={loading || !blockReason}
                >
                  {loading ? 'Blocking...' : 'Block Member'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MemberList;